import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useProjectStageStore } from '@/components/common/menu/lib/store/useProjectStageStore.tsx'
import { resolveStageWriteBlock, type StageProjectLicenceT } from '@/lib/licence/licence.ts'

// setTimeout overflows above 2^31-1 ms (~24.8 days) and fires at once; a longer wait re-arms.
const MAX_TIMEOUT_MS = 2 ** 31 - 1

/**
 * "Now" for judging the demo clock, re-rendering the caller when `expiresAt` passes. Without
 * it a tab left open across the end of a demo keeps the add buttons enabled until something
 * else happens to re-render it.
 */
export function useLicenceClock(expiresAt: string | null | undefined): Date {
  const [tick, setTick] = useState(0)
  useEffect(() => {
    if (!expiresAt) return
    const wait = new Date(expiresAt).getTime() - Date.now()
    if (!(wait > 0)) return
    // Re-armed through `tick` until the end has really passed (covers the capped wait).
    const timer = setTimeout(() => setTick(n => n + 1), Math.min(wait, MAX_TIMEOUT_MS))
    return () => clearTimeout(timer)
  }, [expiresAt, tick])
  return new Date()
}

/**
 * The licence fields of the stage on screen, or null off a stage route. Read from the
 * stage store, and only when it holds the stage the URL names: the store keeps the last
 * stage after the user leaves it, and right after a stage switch it still holds the old one.
 */
export function useStageLicence(): StageProjectLicenceT | null {
  const { projectStageId } = useParams()
  const stage = useProjectStageStore(s => s.currProjectStage)
  if (!projectStageId || !stage || String(stage.id) !== String(projectStageId)) return null
  return ((stage as { project?: StageProjectLicenceT | null }).project ??
    null) as StageProjectLicenceT | null
}

/**
 * Why adding new content on the current stage would be refused, or null. A warning ahead of
 * the server's refusal, used to disable add buttons; the server still decides.
 */
export function useStageWriteBlock() {
  const project = useStageLicence()
  const now = useLicenceClock(project?.demo?.expiresAt)
  return resolveStageWriteBlock(project, now)
}

/** False on a stage where new content would be refused for licence reasons. */
export function useLicenceAllowsCreate(): boolean {
  return useStageWriteBlock() === null
}
